import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Lock } from 'lucide-react';
import { Button } from '../ui/button';

export const IdleTimeoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [isLocked, setIsLocked] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

  const resetTimer = () => {
    if (isLocked || !user) return; // Don't reset if already locked or not logged in

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    timeoutRef.current = setTimeout(() => {
      setIsLocked(true);
    }, IDLE_TIMEOUT_MS);
  };

  useEffect(() => {
    if (!user) return;

    resetTimer();

    const events = ['mousemove', 'keydown', 'wheel', 'DOMMouseScroll', 'mouseWheel', 'mousedown', 'touchstart', 'touchmove', 'MSPointerDown', 'MSPointerMove'];
    
    const handleActivity = () => resetTimer();

    events.forEach(e => document.addEventListener(e, handleActivity));

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      events.forEach(e => document.removeEventListener(e, handleActivity));
    };
  }, [user, isLocked]);

  const handleForcedLogout = async () => {
    await signOut();
    setIsLocked(false);
    navigate('/login');
    toast({ title: "Session Expired", description: "You were logged out due to inactivity for security reasons." });
  };

  return (
    <>
      {isLocked && (
        <div className="fixed inset-0 z-[9999] bg-slate-950/80 backdrop-blur-md flex items-center justify-center animate-in fade-in duration-500">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 shadow-2xl text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6 ring-8 ring-red-50">
              <Lock className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">Session Locked</h2>
            <p className="text-slate-500 mb-8 font-medium">For your security, your session has been locked due to 10 minutes of inactivity.</p>
            
            <div className="flex flex-col w-full gap-3">
              <Button onClick={() => window.location.reload()} className="w-full bg-slate-900 hover:bg-slate-800 h-12 rounded-xl text-md font-bold">
                Unlock Session
              </Button>
              <Button variant="ghost" onClick={handleForcedLogout} className="w-full h-11 rounded-xl text-slate-500 hover:text-red-600">
                Sign Out Completely
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className={isLocked ? 'blur-sm pointer-events-none select-none transition-all duration-500' : ''}>
        {children}
      </div>
    </>
  );
};
